import { DEFAULT_CURRENCY, formatMoney, toMinorUnits } from "@vouch/shared";
import { ArrowLeft, Check, Dices, Play, Plus, UserPlus, X } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Input, Modal, Select, Spinner } from "../components/ui";
import {
  useAccounts,
  useCommittee,
  useCommittees,
  useContribute,
  useCreateCommittee,
  useDrawRound,
  useInviteMember,
  useRespondInvite,
  useStartCommittee,
} from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { confirm } from "../lib/confirm";
import { toast } from "../lib/toast";

export function CommitteesPage(): JSX.Element {
  const { data: committees, isLoading } = useCommittees();
  const { data: accounts = [] } = useAccounts();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const create = useCreateCommittee();

  if (isLoading) return <Spinner />;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-500">Committees</h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus size={16} /> New committee
        </Button>
      </div>

      <div className="grid gap-3">
        {(committees ?? []).map((c) => (
          <Card key={c.id}>
            <button className="flex w-full items-center justify-between text-left" onClick={() => navigate(`/committees/${c.id}`)}>
              <div>
                <p className="font-medium text-slate-700">{c.name}</p>
                <p className="text-xs text-slate-400">
                  {c.totalSlots} slots · {c.payoutMode} · {c.status}
                </p>
              </div>
              <p className="font-semibold text-brand-500">
                {formatMoney(c.contributionAmount, c.currency)}
                <span className="text-xs font-normal text-slate-400">/{c.frequency}</span>
              </p>
            </button>
          </Card>
        ))}
        {committees?.length === 0 && (
          <Card>
            <p className="text-sm text-slate-500">
              No committees yet. Start a BC/committee and invite members.
            </p>
          </Card>
        )}
      </div>

      {createOpen && (
        <CreateCommitteeModal
          accounts={accounts}
          busy={create.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (vals) => {
            try {
              await create.mutateAsync(vals);
              toast.success("Committee created");
              setCreateOpen(false);
            } catch (e) {
              toast.error(e instanceof Error ? e.message : "Failed to create committee");
            }
          }}
        />
      )}
    </div>
  );
}

export function CommitteeDetailPage(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  return <CommitteeDetailView id={id} onBack={() => navigate("/committees")} />;
}

function CommitteeDetailView({ id, onBack }: { id: string; onBack: () => void }): JSX.Element {
  const { user } = useAuth();
  const { data, isLoading } = useCommittee(id);
  const { data: accounts = [] } = useAccounts();
  const invite = useInviteMember();
  const respond = useRespondInvite();
  const contribute = useContribute();
  const start = useStartCommittee();
  const draw = useDrawRound();
  const [inviteVia, setInviteVia] = useState<"username" | "email">("username");
  const [inviteValue, setInviteValue] = useState("");
  const [acceptOpen, setAcceptOpen] = useState(false);

  if (isLoading || !data) return <Spinner />;
  const { committee, members, rounds, contributions } = data;
  const isOrganizer = committee.organizerUserId === user?.id;
  const myMembership = members.find((m) => m.userId === user?.id);
  const memberName = (mid: string | null): string =>
    members.find((m) => m.id === mid)?.name ?? "—";
  const hasPaid = (roundId: string): boolean =>
    contributions.some(
      (c) => c.roundId === roundId && c.memberId === myMembership?.id && c.status === "paid",
    );
  const completedRounds = rounds.filter((round) => round.status === "paid").length;
  const currentRound = rounds.find((round) => round.status !== "paid");
  const remainingRounds = Math.max(0, rounds.length - completedRounds);
  const acceptedMembers = members.filter((member) => member.joinStatus === "accepted").length;
  const totalPot = committee.contributionAmount * acceptedMembers;

  const markPaid = async (roundId: string): Promise<void> => {
    const ok = await confirm({
      title: "Mark contribution paid?",
      message: `${formatMoney(committee.contributionAmount, committee.currency)} will be debited from your committee account.`,
      confirmLabel: "Mark paid",
    });
    if (!ok) return;
    try {
      await contribute.mutateAsync({ id, vars: { roundId } });
      toast.success("Contribution recorded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed — pick a committee account when you joined");
    }
  };

  const doStart = async (): Promise<void> => {
    const ok = await confirm({
      title: "Start committee?",
      message: "This locks the roster and generates all rounds. You can't add members after.",
      confirmLabel: "Start",
    });
    if (!ok) return;
    try {
      await start.mutateAsync({ id, vars: undefined });
      toast.success("Committee started");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start (need at least 2 accepted members)");
    }
  };

  const doInvite = async (): Promise<void> => {
    if (!inviteValue.trim()) return;
    try {
      await invite.mutateAsync({
        id,
        vars: inviteVia === "username" ? { username: inviteValue.trim() } : { email: inviteValue.trim() },
      });
      toast.success("Invitation sent");
      setInviteValue("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not invite");
    }
  };

  const doDecline = async (): Promise<void> => {
    const ok = await confirm({ title: "Decline invitation?", confirmLabel: "Decline", danger: true });
    if (!ok) return;
    try {
      await respond.mutateAsync({ id, vars: { accept: false } });
      toast.success("Invitation declined");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const acceptWithAccounts = async (
    contributionAccountId?: string,
    payoutAccountId?: string,
  ): Promise<void> => {
    try {
      await respond.mutateAsync({ id, vars: { accept: true, contributionAccountId, payoutAccountId } });
      toast.success("You've joined the committee");
      setAcceptOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const doDraw = async (roundId: string): Promise<void> => {
    const ok = await confirm({ title: "Draw this round?", confirmLabel: "Draw" });
    if (!ok) return;
    try {
      await draw.mutateAsync({ id, vars: { roundId } });
      toast.success("Round drawn");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Draw failed");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-brand-500">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-500">{committee.name}</h1>
          <p className="text-sm text-slate-400">
            {formatMoney(committee.contributionAmount, committee.currency)} / {committee.frequency} ·{" "}
            {committee.payoutMode} · {committee.status}
          </p>
        </div>
        {isOrganizer && committee.status === "forming" && (
          <Button onClick={() => void doStart()} disabled={start.isPending}>
            <Play size={16} /> Start
          </Button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Progress</p><p className="mt-1 text-xl font-bold text-slate-800">{completedRounds}/{rounds.length || committee.totalSlots}</p><p className="text-xs text-slate-500">rounds completed</p></Card>
        <Card className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Remaining turns</p><p className="mt-1 text-xl font-bold text-blue-700">{remainingRounds || committee.totalSlots}</p><p className="text-xs text-slate-500">lotteries or payouts</p></Card>
        <Card className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Current round</p><p className="mt-1 text-xl font-bold text-amber-600">{currentRound ? `#${currentRound.roundNumber}` : committee.status === "completed" ? "Done" : "Not started"}</p><p className="text-xs text-slate-500">{currentRound ? new Date(currentRound.scheduledDate).toLocaleDateString() : "—"}</p></Card>
        <Card className="p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Pot per turn</p><p className="mt-1 text-xl font-bold text-emerald-600">{formatMoney(totalPot, committee.currency)}</p><p className="text-xs text-slate-500">{acceptedMembers} accepted members</p></Card>
      </div>

      {rounds.length > 0 && (
        <Card>
          <div className="flex items-end justify-between gap-3">
            <div><h2 className="font-semibold text-slate-800">Committee life</h2><p className="text-sm text-slate-500">Past, current, and future turns in one view.</p></div>
            <span className="text-xs font-medium text-slate-500">{Math.round((completedRounds / rounds.length) * 100)}% complete</span>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-emerald-500" style={{ width: `${(completedRounds / rounds.length) * 100}%` }} /></div>
          <div className="mt-5 flex gap-3 overflow-x-auto pb-2">
            {rounds.map((round) => {
              const phase = round.status === "paid" ? "Past" : round.id === currentRound?.id ? "Now" : "Future";
              const style = phase === "Past" ? "border-emerald-200 bg-emerald-50" : phase === "Now" ? "border-blue-300 bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white";
              return <div key={round.id} className={`min-w-40 rounded-2xl border p-3 ${style}`}><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{phase}</p><p className="mt-1 font-semibold text-slate-800">Round {round.roundNumber}</p><p className="text-xs text-slate-500">{new Date(round.scheduledDate).toLocaleDateString()}</p><p className="mt-2 text-xs font-medium text-slate-600">{round.payoutMemberId ? memberName(round.payoutMemberId) : round.status === "paid" ? "Completed" : "Turn not assigned"}</p></div>;
            })}
          </div>
        </Card>
      )}

      {/* Pending invitation for the current user. */}
      {myMembership?.joinStatus === "invited" && (
        <Card className="border-amber-200 bg-amber-50">
          <p className="mb-3 text-sm font-medium text-amber-800">
            You've been invited to join "{committee.name}".
          </p>
          <div className="flex gap-2">
            <Button onClick={() => setAcceptOpen(true)} disabled={respond.isPending}>
              <Check size={16} /> Accept
            </Button>
            <Button variant="ghost" onClick={() => void doDecline()} disabled={respond.isPending}>
              <X size={16} /> Decline
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <h2 className="mb-3 font-semibold text-slate-700">Members</h2>
        <ul className="divide-y divide-slate-100">
          {members.map((m) => (
            <li key={m.id} className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-700">{m.name}</span>
              <span className="text-xs text-slate-400">
                {m.joinStatus}
                {m.payoutReceived && " · paid out"}
              </span>
            </li>
          ))}
        </ul>
        {isOrganizer && committee.status === "forming" && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Select
              className="w-auto"
              value={inviteVia}
              onChange={(e) => setInviteVia(e.target.value as "username" | "email")}
            >
              <option value="username">Username</option>
              <option value="email">Email</option>
            </Select>
            <Input
              className="flex-1"
              placeholder={inviteVia === "username" ? "username" : "email@example.com"}
              value={inviteValue}
              onChange={(e) => setInviteValue(e.target.value)}
            />
            <Button variant="ghost" disabled={!inviteValue || invite.isPending} onClick={() => void doInvite()}>
              <UserPlus size={16} /> Invite
            </Button>
          </div>
        )}
      </Card>

      {rounds.length > 0 && (
        <Card>
          <h2 className="mb-3 font-semibold text-slate-700">Rounds</h2>
          <ul className="divide-y divide-slate-100">
            {rounds.map((r) => {
              const accepted = members.filter((m) => m.joinStatus === "accepted");
              const paidIds = new Set(
                contributions.filter((c) => c.roundId === r.id && c.status === "paid").map((c) => c.memberId),
              );
              const paidNames = accepted.filter((m) => paidIds.has(m.id)).map((m) => m.name);
              const pendingNames = accepted.filter((m) => !paidIds.has(m.id)).map((m) => m.name);
              const collected = paidIds.size * committee.contributionAmount;
              return (
              <li key={r.id} className="flex items-start justify-between py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700">Round {r.roundNumber}</p>
                  <p className="text-xs text-slate-400">
                    Due {new Date(r.scheduledDate).toLocaleDateString()}
                    {r.payoutMemberId && ` · winner: ${memberName(r.payoutMemberId)}`}
                    {r.lotterySeed && ` · seed ${r.lotterySeed.slice(0, 8)}…`}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Collected {formatMoney(collected, committee.currency)} · {paidIds.size}/{accepted.length} paid
                  </p>
                  {paidNames.length > 0 && (
                    <p className="text-[11px] text-green-600">paid: {paidNames.join(", ")}</p>
                  )}
                  {pendingNames.length > 0 && (
                    <p className="text-[11px] text-amber-600">awaiting: {pendingNames.join(", ")}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Members mark their own contribution paid for this round. */}
                  {myMembership?.joinStatus === "accepted" &&
                    (hasPaid(r.id) ? (
                      <span className="text-xs font-medium text-green-600">you paid ✓</span>
                    ) : (
                      <Button variant="ghost" onClick={() => void markPaid(r.id)} disabled={contribute.isPending}>
                        Mark paid
                      </Button>
                    ))}
                  {r.status === "paid" ? (
                    <span className="text-xs font-medium text-green-600">drawn</span>
                  ) : (
                    isOrganizer && (
                      <Button variant="ghost" onClick={() => void doDraw(r.id)} disabled={draw.isPending}>
                        <Dices size={16} /> Draw
                      </Button>
                    )
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        </Card>
      )}

      {acceptOpen && (
        <AcceptCommitteeModal
          accounts={accounts}
          busy={respond.isPending}
          onClose={() => setAcceptOpen(false)}
          onSubmit={acceptWithAccounts}
        />
      )}
    </div>
  );
}

function AcceptCommitteeModal({
  accounts,
  busy,
  onClose,
  onSubmit,
}: {
  accounts: { id: string; name: string }[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (contributionAccountId?: string, payoutAccountId?: string) => Promise<void>;
}): JSX.Element {
  const [contributionAccountId, setContributionAccountId] = useState(accounts[0]?.id ?? "");
  const [payoutAccountId, setPayoutAccountId] = useState(accounts[0]?.id ?? "");
  return (
    <Modal open title="Join committee" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">Choose which accounts to use for this committee.</p>
        <Select label="Pay contributions from" value={contributionAccountId} onChange={(e) => setContributionAccountId(e.target.value)}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </Select>
        <Select label="Receive payout into" value={payoutAccountId} onChange={(e) => setPayoutAccountId(e.target.value)}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </Select>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={() => void onSubmit(contributionAccountId || undefined, payoutAccountId || undefined)}
          >
            {busy ? "Saving…" : "Join"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateCommitteeModal({
  accounts,
  busy,
  onClose,
  onSubmit,
}: {
  accounts: { id: string; name: string }[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (vals: {
    name: string;
    contributionAmountMinor: number;
    currency: string;
    totalSlots: number;
    payoutMode: "fixed" | "agreed" | "lottery";
    contributionAccountId?: string;
    payoutAccountId?: string;
  }) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [slots, setSlots] = useState("4");
  const [payoutMode, setPayoutMode] = useState<"fixed" | "agreed" | "lottery">("lottery");
  const [contributionAccountId, setContributionAccountId] = useState(accounts[0]?.id ?? "");
  const [payoutAccountId, setPayoutAccountId] = useState(accounts[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal open title="New committee" onClose={onClose}>
      <div className="space-y-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Contribution per round" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input label="Number of slots" type="number" min="2" value={slots} onChange={(e) => setSlots(e.target.value)} />
        <Select label="Payout mode" value={payoutMode} onChange={(e) => setPayoutMode(e.target.value as "fixed" | "agreed" | "lottery")}>
          <option value="lottery">Lottery (random draw)</option>
          <option value="fixed">Fixed order</option>
          <option value="agreed">Agreed (organizer picks)</option>
        </Select>
        <Select label="Pay contributions from" value={contributionAccountId} onChange={(e) => setContributionAccountId(e.target.value)}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </Select>
        <Select label="Receive payouts into" value={payoutAccountId} onChange={(e) => setPayoutAccountId(e.target.value)}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </Select>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              const contributionAmountMinor = toMinorUnits(Number(amount));
              const totalSlots = Number(slots);
              if (!name.trim() || contributionAmountMinor <= 0 || totalSlots < 2) {
                setError("Name, a positive contribution and at least 2 slots are required.");
                return;
              }
              void onSubmit({
                name: name.trim(),
                contributionAmountMinor,
                currency: DEFAULT_CURRENCY,
                totalSlots,
                payoutMode,
                contributionAccountId: contributionAccountId || undefined,
                payoutAccountId: payoutAccountId || undefined,
              });
            }}
          >
            {busy ? "Saving…" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
